import React, { useState, useRef, useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";

interface Option {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface GroupedOption {
  group: string;
  options: Option[];
}

interface TeamMultipleDropdownProps {
  label: string;
  options: GroupedOption[]; // Updated to accept grouped options
  values: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const TeamMultipleDropdown: React.FC<TeamMultipleDropdownProps> = ({
  label,
  options,
  values,
  onChange,
  style,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<"up" | "down">(
    "down",
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  // Filter options based on the search term
  const filteredOptions = options
    ?.map((group) => ({
      ...group,
      options: group.options?.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    }))
    ?.filter((group) => group.options?.length > 0); // Only show groups with options

  const handleToggle = (value: string | number) => {
    if (!disabled) {
      if (values.includes(value)) {
        onChange(values.filter((item) => item !== value));
      } else {
        onChange([...values, value]);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm(""); // Clear search input when dropdown is closed
    }
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target as Node;

      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const handleDropdownToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => {
        if (prev) {
          setSearchTerm(""); // Clear the search term when closing the dropdown
        }
        return !prev;
      });
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current && dropdownMenuRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const menuHeight = dropdownMenuRef.current.offsetHeight;
      const windowHeight = window.innerHeight;

      if (dropdownRect.bottom + menuHeight > windowHeight) {
        setDropdownPosition("up");
      } else {
        setDropdownPosition("down");
      }
    }
  }, [isOpen]);

  return (
    <Form.Group
      controlId="teamMultipleDropdown"
      style={{ ...style, position: "relative" }}
      ref={dropdownRef}
    >
      <div
        className={`form-control d-flex align-items-center justify-content-between ${
          disabled ? "disabled" : ""
        }`}
        onClick={handleDropdownToggle}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <span>{label}</span>
        <span className="material-icons">
          {isOpen ? "expand_less" : "expand_more"}
        </span>
      </div>
      {isOpen && (
        <div
          ref={dropdownMenuRef}
          className="border p-0 bg-white position-absolute"
          style={{
            maxHeight: "200px",
            width: "100%",
            zIndex: 10,
            borderRadius: "0px 0px 9px 9px",
            boxShadow: " 0px 5px 5px #ccc",
            overflowY: "auto",
            bottom: dropdownPosition === "up" ? "100%" : "unset",
            top: dropdownPosition === "down" ? "100%" : "unset",
            marginBottom: dropdownPosition === "up" ? "5px" : "0",
            marginTop: dropdownPosition === "down" ? "5px" : "0",
          }}
        >
          <InputGroup className="p-2">
            <Form.Control
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
            />
          </InputGroup>
          {filteredOptions.map((group) => (
            <div key={group.group}>
              <div className="teamTitle">{group.group}</div>{" "}
              {/* Group heading */}
              {group.options.map((option) => (
                <div key={option.value} className="form-check">
                  <input
                    type="checkbox"
                    id={`checkbox-${option.value}`}
                    className="form-check-input"
                    checked={values.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                    disabled={option?.disabled}
                  />
                  <label
                    htmlFor={`checkbox-${option.value}`}
                    className="form-check-label"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          ))}
          {filteredOptions?.length === 0 && (
            <p className="text-muted text-center">No options found.</p>
          )}
        </div>
      )}
    </Form.Group>
  );
};

export default TeamMultipleDropdown;
